import React, { useEffect, useState, useCallback } from "react";
import { RefreshCcw, Radar, Sparkles, Search, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ComingSoonCard from "@/components/ComingSoonCard";

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
      active
        ? "bg-white text-black"
        : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const ComingSoon = () => {
  const { user, login } = useAuth();
  const [tab, setTab] = useState("global"); // global | personal
  const [globalFeed, setGlobalFeed] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [needsFollow, setNeedsFollow] = useState(false);

  // Quick "scan an artist" form
  const [scanQuery, setScanQuery] = useState("");
  const [scanLoading, setScanLoading] = useState(false);

  const loadGlobal = useCallback(async () => {
    try {
      const r = await api.get("/upcoming/feed", { params: { limit: 60 } });
      setGlobalFeed(r.data.results || []);
    } catch {
      toast.error("Failed to load global feed");
    }
  }, []);

  const loadPersonal = useCallback(async () => {
    try {
      const r = await api.get("/upcoming/personalized", { params: { limit: 60 } });
      setPersonal(r.data.results || []);
      setNeedsFollow(!!r.data.needs_follow);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadGlobal(), user ? loadPersonal() : Promise.resolve()])
      .finally(() => setLoading(false));
  }, [loadGlobal, loadPersonal, user]);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/upcoming/refresh-all", null, { params: { max_artists: 12 } });
      toast.success(`Refreshed ${r.data.scanned} artists`);
      await loadGlobal();
      if (user) await loadPersonal();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanQuery.trim()) return;
    setScanLoading(true);
    try {
      const r = await api.post("/upcoming/scan", null, {
        params: { artist_name: scanQuery.trim() },
      });
      const found = (r.data?.releases || []).length;
      if (found > 0) {
        toast.success(`Found ${found} upcoming release${found > 1 ? "s" : ""} for ${r.data.artist_name}`);
      } else {
        toast.message(`No upcoming releases found for ${r.data.artist_name}`, {
          description: "AI scanned news + Apple Music but found no announced releases yet.",
        });
      }
      setScanQuery("");
      await loadGlobal();
    } catch {
      toast.error("Scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  const list = tab === "global" ? globalFeed : personal;

  return (
    <div data-testid="coming-soon-page" className="px-4 sm:px-8 py-6 max-w-7xl mx-auto">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl mb-8 p-8 sm:p-12 bg-gradient-to-br from-[#FF2E00]/20 via-[#FF2E00]/15 to-[#E2FF31]/10 border border-white/10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E2FF31] font-bold mb-3">
          <Radar size={14} /> AI Release Tracker
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3">
          Coming Soon.
        </h1>
        <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
          Our AI scans Apple Music, Wikipedia, fan blogs and the latest news to surface{" "}
          <span className="text-white font-semibold">unreleased music</span> from the artists you love —
          before they hit your feed.
        </p>

        <div className="flex items-center gap-3 mt-6 flex-wrap">
          <button
            data-testid="coming-soon-refresh-btn"
            onClick={refreshAll}
            disabled={refreshing}
            className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-5 py-2.5 hover:scale-[1.03] transition disabled:opacity-60"
          >
            <RefreshCcw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh feed"}
          </button>
          <span className="text-xs text-white/40">Auto-refreshes every 24h</span>
        </div>
      </div>

      {/* Quick-scan form */}
      <form onSubmit={handleScan} className="mb-8 flex gap-2 max-w-2xl">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="coming-soon-scan-input"
            type="text"
            value={scanQuery}
            onChange={(e) => setScanQuery(e.target.value)}
            placeholder="Scan any artist for upcoming releases (e.g. Taylor Swift)"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition"
          />
        </div>
        <button
          data-testid="coming-soon-scan-btn"
          type="submit"
          disabled={scanLoading || !scanQuery.trim()}
          className="bg-[#FF2E00] hover:bg-[#FF2E00]/90 text-white font-semibold rounded-full px-5 disabled:opacity-50 transition flex items-center gap-2"
        >
          <Sparkles size={14} /> {scanLoading ? "Scanning…" : "Scan"}
        </button>
      </form>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6">
        <TabBtn active={tab === "global"} onClick={() => setTab("global")} testId="coming-soon-tab-global">
          Global feed
        </TabBtn>
        <TabBtn active={tab === "personal"} onClick={() => setTab("personal")} testId="coming-soon-tab-personal">
          For you
        </TabBtn>
        <span className="text-xs text-white/40 ml-auto">
          {list.length} release{list.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Empty / auth states */}
      {tab === "personal" && !user && (
        <div className="text-center py-16 px-4 rounded-2xl bg-white/[0.03] border border-white/8">
          <UserPlus size={36} className="mx-auto text-white/40 mb-4" />
          <h3 className="text-xl font-semibold mb-2">Sign in for personalized picks</h3>
          <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
            Follow your favorite artists and we&apos;ll alert you the moment their next release is announced.
          </p>
          <button
            onClick={login}
            className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.03] transition"
            data-testid="coming-soon-signin-btn"
          >
            Sign in
          </button>
        </div>
      )}

      {tab === "personal" && user && needsFollow && personal.length === 0 && (
        <div className="text-center py-16 px-4 rounded-2xl bg-white/[0.03] border border-white/8">
          <Sparkles size={36} className="mx-auto text-[#E2FF31] mb-4" />
          <h3 className="text-xl font-semibold mb-2">Follow some artists</h3>
          <p className="text-white/60 text-sm mb-2 max-w-md mx-auto">
            Visit any artist page and tap{" "}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white text-xs font-bold">
              <Radar size={11} /> Track
            </span>{" "}
            — or like a few songs and we&apos;ll auto-track those artists for you.
          </p>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-white/50 text-sm">Loading…</div>
      ) : list.length === 0 && !(tab === "personal" && (!user || needsFollow)) ? (
        <div className="text-center py-16 text-white/50 text-sm">
          No upcoming releases tracked yet. Use the search above to scan an artist.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((r, i) => (
            <ComingSoonCard
              key={`${r.artist_id}-${r.release_title}-${i}`}
              release={r}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ComingSoon;
